import { inject, Injectable, Injector } from '@angular/core';
import { SharedStorage } from '../interfaces/share-storage.model';
import { decrypt, encrypt } from '../utils/crypto.util';
import { STORAGE_KEY } from '../constants/store-key.constants';
import { AuthService } from './auth.service';

/**
 * 存儲組件間共用數據的 service
 */

// localStorage 唯一 key
const LS_KEY = 'sharedStorage';

@Injectable({
  providedIn: 'root',
})
export class SharedStorageService {
  private isBrowser = typeof window !== 'undefined';
  private store: Partial<SharedStorage> = {};

  constructor(private injector: Injector) {
    // 伺服器端執行，直接跳過
    if (!this.isBrowser) return;

    // 還原 localStorage 資料
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      const json = decrypt(saved);
      if (json) {
        try {
          this.store = JSON.parse(json);
        } catch {
          this.store = {};
        } // 解析失敗重置
      }
    }

    console.log(this.store);
  }

  set<K extends keyof SharedStorage>(key: K, value: SharedStorage[K]): void {
    this.store[key] = value;
    this.persist();
  }

  get<K extends keyof SharedStorage>(key: K): SharedStorage[K] | undefined {
    return this.store[key];
  }

  remove<K extends keyof SharedStorage>(key: K): void {
    delete this.store[key];
    this.persist();
  }

  // 清除所有存儲的數據，只保留homeShowAnimation
  clear(): void {
    const homeShowAnimation = this.store.homeShowAnimation;
    const createMemberRequest = this.store.createMemberRequest;
    const loginStatus = this.store.loginStatus;
    const myReservation = this.store.myReservation;
    const savedQuestionnaireData = this.store.savedQuestionnaireData; // 保留健調表資料
    const fromIdLogin = this.store.fromIdLogin;
    const healthPlan = this.store.healthPlan; // 保留活動

    this.store = {};

    if (homeShowAnimation !== undefined) {
      this.store.homeShowAnimation = homeShowAnimation;
    }

    if (myReservation !== undefined) {
      this.store.myReservation = myReservation;
    }

    // 保留健調表資料，避免導航時被清除
    if (savedQuestionnaireData !== undefined) {
      this.store.savedQuestionnaireData = savedQuestionnaireData;
    }

    // 保留活動
    if (healthPlan !== undefined) {
      this.store.healthPlan = healthPlan;
    }

    // 保留fromIdLogin標記，避免重新整理時重新導向
    if (fromIdLogin !== undefined) {
      this.store.fromIdLogin = fromIdLogin;
    }

    // 如果是透過左上角非登出返回首頁且還有登入，保留createMemberRequest 跟 loginStatus (memberLogin)
    const authService = this.injector.get(AuthService);
    const hasToken = this.isBrowser && authService.isLogin();
    if (hasToken) {
      this.store.loginStatus = loginStatus;
      this.store.createMemberRequest = createMemberRequest;
    }

    this.persist();
  }

  has<K extends keyof SharedStorage>(key: K): boolean {
    return key in this.store;
  }

  // 序列化 + 加密 + 儲存 localStorage
  private persist(): void {
    // 伺服器端不要寫 localStorage
    if (!this.isBrowser) return;

    // 真的空了，直接移除 localStorage
    if (Object.keys(this.store).length === 0) {
      localStorage.removeItem(LS_KEY);
      return;
    }

    const json = JSON.stringify(this.store);
    localStorage.setItem(LS_KEY, encrypt(json));
  }
}
